// =============================================================================
// FILE: packages/renderer/src/components/marketing/NewsletterModal.tsx
// PURPOSE: R2 News Feed — lightweight public newsletter signup modal. No login
//   required. Posts to /api/news/subscribe. Reusable across public marketing
//   surfaces (Academy hub, Login landing).
// =============================================================================

import { useState, type JSX } from 'react';
import { X, Mail, CheckCircle2, Loader2, Rss } from 'lucide-react';

const BACKEND: string = (() => {
  const w = typeof window !== 'undefined'
    ? (window as unknown as Record<string, unknown>).__BACKEND_URL__ as string
    : '';
  return w || ((import.meta.env.VITE_BACKEND_URL as string) ?? '');
})();

const CATEGORIES = [
  'R2 Product Updates', 'BIM OPS New Features', 'R2 Academy Articles',
  'AI Infrastructure Trends', 'RCDD Industry Insights', 'Roadmap Announcements',
  'Tutorials and Training',
];

export function NewsletterModal({ open, onClose }: { open: boolean; onClose: () => void }): JSX.Element | null {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName]   = useState('');
  const [email, setEmail]         = useState('');
  const [company, setCompany]     = useState('');
  const [role, setRole]           = useState('');
  const [busy, setBusy]           = useState(false);
  const [done, setDone]           = useState(false);
  const [err, setErr]             = useState('');

  if (!open) return null;

  const reset = (): void => {
    setFirstName(''); setLastName(''); setEmail(''); setCompany(''); setRole('');
    setDone(false); setErr(''); setBusy(false);
  };
  const close = (): void => { reset(); onClose(); };

  const submit = async (): Promise<void> => {
    setErr('');
    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      setErr('First name, last name and email are required.');
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${BACKEND}/api/news/subscribe`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: firstName, last_name: lastName, email,
          company, role,
        }),
      });
      const d = await r.json();
      if (!r.ok) { setErr(d.detail ?? 'Subscription failed. Please try again.'); return; }
      setDone(true);
    } catch {
      setErr('Subscription failed. Please try again.');
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="newsletter-modal" className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: 'rgba(4,6,10,0.72)', backdropFilter: 'blur(6px)' }} onClick={close}>
      <div className="w-full max-w-md rounded-2xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}
        style={{ background: '#0c0e12', border: '1px solid rgba(45,181,168,0.3)' }}>
        {/* header */}
        <div className="flex items-center gap-2 px-5 py-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', background: 'linear-gradient(90deg, rgba(45,181,168,0.10), transparent)' }}>
          <span className="grid place-items-center h-8 w-8 rounded-lg" style={{ background: 'rgba(45,181,168,0.12)', border: '1px solid rgba(45,181,168,0.35)' }}>
            <Rss className="h-4 w-4 text-teal-300" />
          </span>
          <div className="leading-tight">
            <div className="text-[14px] font-semibold text-zinc-100">R2 News Feed</div>
            <div className="text-[11px] text-zinc-500">Follow the evolution of BIM OPS</div>
          </div>
          <button data-testid="newsletter-close" onClick={close} className="ml-auto text-zinc-500 hover:text-zinc-200 transition"><X className="h-4.5 w-4.5" /></button>
        </div>

        {done ? (
          <div data-testid="newsletter-success" className="px-6 py-10 text-center">
            <CheckCircle2 className="h-12 w-12 mx-auto text-teal-400 mb-4" />
            <div className="text-[15px] font-semibold text-zinc-100 mb-2">Welcome to the R2 News Feed.</div>
            <p className="text-[13px] text-zinc-400 leading-relaxed">
              You will receive updates on BIM OPS, R2 innovations, tutorials, and the future of infrastructure intelligence.
            </p>
            <button data-testid="newsletter-done" onClick={close} className="mt-6 px-5 py-2.5 rounded-full text-[13px] font-semibold text-zinc-900" style={{ background: 'linear-gradient(90deg,#2dd4bf,#5eead4)' }}>Done</button>
          </div>
        ) : (
          <div className="px-5 py-5">
            <p className="text-[12.5px] text-zinc-400 mb-4 leading-relaxed">
              Subscribe for updates on R2, BIM OPS, AI infrastructure design, product releases, and industry insights.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Input testid="news-first-name" label="First Name *" value={firstName} onChange={setFirstName} />
              <Input testid="news-last-name" label="Last Name *" value={lastName} onChange={setLastName} />
            </div>
            <div className="mt-3"><Input testid="news-email" label="Email Address *" type="email" value={email} onChange={setEmail} /></div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <Input testid="news-company" label="Company" value={company} onChange={setCompany} />
              <Input testid="news-role" label="Professional Role" value={role} onChange={setRole} />
            </div>

            <div className="mt-4 flex flex-wrap gap-1.5">
              {CATEGORIES.map((c) => (
                <span key={c} className="px-2 py-0.5 rounded-full text-[10px] text-teal-200/80" style={{ background: 'rgba(45,181,168,0.07)', border: '1px solid rgba(45,181,168,0.2)' }}>{c}</span>
              ))}
            </div>

            {err && <div data-testid="newsletter-error" className="mt-4 px-3 py-2 rounded-lg text-[12px] text-rose-300" style={{ background: 'rgba(190,18,60,0.12)', border: '1px solid rgba(190,18,60,0.4)' }}>{err}</div>}

            <button data-testid="newsletter-submit" onClick={() => void submit()} disabled={busy}
              className="mt-5 w-full h-11 rounded-full flex items-center justify-center gap-2 text-[13px] font-semibold text-zinc-900 disabled:opacity-60 transition hover:brightness-110"
              style={{ background: 'linear-gradient(90deg,#2dd4bf,#5eead4)' }}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Subscribe
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Input({ testid, label, value, onChange, type = 'text' }:
  { testid: string; label: string; value: string; onChange: (v: string) => void; type?: string }): JSX.Element {
  return (
    <label className="block">
      <span className="block text-[10.5px] uppercase tracking-[0.14em] text-zinc-500 mb-1">{label}</span>
      <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full h-10 px-3 rounded-lg text-[13px] text-zinc-100 focus:outline-none transition"
        style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.1)' }} />
    </label>
  );
}
